import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export interface StockStatus {
  status: 'green' | 'amber' | 'red' | 'no_data';
  coverage_percentage: number;
  quantity_available: number;
  four_week_forecast: number;
}

interface StockStatusIndicatorProps {
  stockStatus?: StockStatus;
  showText?: boolean;
}

const STATUS_COLORS = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  no_data: 'bg-slate-300',
};

const STATUS_LABELS = {
  green: 'Sufficient Stock',
  amber: 'Low Stock',
  red: 'Critical Stock',
  no_data: 'No Stock Data',
};

export default function StockStatusIndicator({
  stockStatus,
  showText = false,
}: StockStatusIndicatorProps) {
  if (!stockStatus) {
    return null;
  }

  const { status, coverage_percentage, quantity_available, four_week_forecast } = stockStatus;
  const colorClass = STATUS_COLORS[status] || STATUS_COLORS.no_data;
  const label = STATUS_LABELS[status] || 'Unknown';

  const formatNumber = (num: number) => {
    return num.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 cursor-help">
            <div
              className={`w-3 h-3 rounded-full ${colorClass} ring-2 ring-white shadow-sm`}
            />
            {showText && (
              <span className="text-xs text-muted-foreground">
                {coverage_percentage.toFixed(0)}%
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1">
            <div className="font-medium flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${colorClass}`} />
              {label}
            </div>
            <div className="text-xs text-muted-foreground space-y-0.5">
              <div className="flex justify-between gap-4">
                <span>Stock Available:</span>
                <span className="font-mono">{formatNumber(quantity_available)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span>4-Week Forecast:</span>
                <span className="font-mono">{formatNumber(four_week_forecast)}</span>
              </div>
              <div className="flex justify-between gap-4 pt-1 border-t">
                <span>Coverage:</span>
                <span className="font-mono font-medium">
                  {coverage_percentage >= 1000 ? '>1000%' : `${coverage_percentage.toFixed(1)}%`}
                </span>
              </div>
            </div>
            {status === 'no_data' && (
              <div className="text-xs text-amber-600 mt-1">
                Supplier has not uploaded stock data for this part
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Legend component for the Forecast tab
export function StockStatusLegend() {
  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground">
      <span className="font-medium">Stock Status:</span>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded-full bg-green-500" />
        <span>&ge;100%</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded-full bg-amber-500" />
        <span>50-99%</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <span>&lt;50%</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded-full bg-slate-300" />
        <span>No data</span>
      </div>
    </div>
  );
}
